"use client";

import { motion, type Variants } from "motion/react";
import { cn } from "@/lib/utils";

// Featured-module bento previews — drop in the lower-right of a featured
// BentoCard. Pointer-events disabled so the card link still owns the click.
// Each preview is hardcoded mock data pulled from divetiqcl/src/lib/agentic-mock.ts
// and erp-mock.ts so what users see on the marketing site matches the product.

const VIEWPORT = { once: true, margin: "-20% 0px" } as const;

const cluster: Variants = {
  hidden: {},
  show: { transition: { delayChildren: 0.2, staggerChildren: 0.06 } },
};

const row: Variants = {
  hidden: { opacity: 0, x: 8 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

function PreviewShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  // The shell is positioned by the parent BentoCard; we add a gradient mask so
  // the bottom of the card stays anchored and the top fades into the card
  // chrome (FlickeringGrid for non-featured, or just card bg).
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute right-3 bottom-3 hidden w-[60%] max-w-[280px] origin-bottom-right select-none sm:block",
        "[mask-image:linear-gradient(to_top,#000_70%,transparent_100%)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HCM — 3 employee rows with payroll status. Rows stagger-fade;
// the bottom-row "Pending" pill pulses to suggest a payroll run.
// ─────────────────────────────────────────────────────────────

export function HcmEmployeePreview() {
  const employees = [
    { name: "Jane Smith", role: "Senior PM", status: "Paid", tone: "ok" as const },
    { name: "Michael Chen", role: "Software Eng.", status: "Paid", tone: "ok" as const },
    { name: "John Doe", role: "Designer", status: "Pending", tone: "warn" as const },
  ];
  return (
    <PreviewShell>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="space-y-1.5 rounded-xl border border-neutral-200/80 bg-white/95 p-2 shadow-[0_8px_24px_-12px_rgba(14,18,27,0.18)] backdrop-blur"
      >
        {employees.map((e) => (
          <motion.div
            key={e.name}
            variants={row}
            className="flex items-center gap-2 rounded-lg px-1.5 py-1.5"
          >
            <span
              aria-hidden
              className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[10px] font-semibold text-brand"
            >
              {e.name
                .split(" ")
                .map((p) => p[0])
                .slice(0, 2)
                .join("")}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[11px] font-semibold leading-tight text-neutral-950">
                {e.name}
              </span>
              <span className="block truncate text-[10px] text-neutral-500">
                {e.role}
              </span>
            </span>
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                e.tone === "ok"
                  ? "bg-accent-green/15 text-accent-green"
                  : "bg-amber-500/15 text-amber-700 animate-pulse",
              )}
            >
              {e.status}
            </span>
          </motion.div>
        ))}
      </motion.div>
    </PreviewShell>
  );
}

// ─────────────────────────────────────────────────────────────
// ERP — 4 mini flow tiles. Each tile has a tiny progress arc that
// draws from 0 → target on view; counter ticks up.
// ─────────────────────────────────────────────────────────────

export function ErpFlowTilesPreview() {
  const tiles = [
    { code: "P2P", label: "Procure → Pay", pct: 78, count: "12 open" },
    { code: "O2C", label: "Order → Cash", pct: 64, count: "8 open" },
    { code: "R2R", label: "Record → Report", pct: 92, count: "3 open" },
    { code: "H2R", label: "Hire → Retire", pct: 41, count: "5 open" },
  ];
  return (
    <PreviewShell className="w-[64%] max-w-[300px]">
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="grid grid-cols-2 gap-1.5"
      >
        {tiles.map((t) => (
          <motion.div
            key={t.code}
            variants={row}
            className="rounded-lg border border-neutral-200/80 bg-white/95 px-2 py-1.5 shadow-[0_4px_12px_-8px_rgba(14,18,27,0.16)]"
          >
            <div className="flex items-center justify-between gap-1">
              <span className="font-mono text-[9px] font-bold tracking-tight text-brand">
                {t.code}
              </span>
              <span className="font-mono text-[9px] tabular-nums text-neutral-500">
                {t.pct}%
              </span>
            </div>
            <p className="mt-0.5 truncate text-[9.5px] font-medium leading-tight text-neutral-950">
              {t.label}
            </p>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-neutral-100">
              <motion.span
                className="block h-full rounded-full bg-brand"
                initial={{ width: 0 }}
                whileInView={{ width: `${t.pct}%` }}
                viewport={VIEWPORT}
                transition={{ duration: 1.1, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
              />
            </div>
          </motion.div>
        ))}
      </motion.div>
    </PreviewShell>
  );
}

// ─────────────────────────────────────────────────────────────
// CRM — AI recommendation card with confidence bar.
// ─────────────────────────────────────────────────────────────

export function CrmRecommendationPreview() {
  return (
    <PreviewShell>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="rounded-xl border border-neutral-200/80 bg-white/95 p-2.5 shadow-[0_8px_24px_-12px_rgba(14,18,27,0.18)]"
      >
        <motion.div variants={row} className="flex items-center gap-1.5">
          <span className="inline-flex size-4 items-center justify-center rounded-md bg-accent-purple/15 text-[9px] font-bold text-accent-purple">
            ★
          </span>
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-accent-purple">
            Next best action
          </span>
        </motion.div>
        <motion.p
          variants={row}
          className="mt-1.5 text-[11px] font-semibold leading-snug text-neutral-950"
        >
          Send renewal offer to Acme Co — last 3 reps converted at 86%.
        </motion.p>
        <motion.div variants={row} className="mt-2.5">
          <div className="flex items-center justify-between text-[9px] text-neutral-500">
            <span>Confidence</span>
            <span className="font-mono font-semibold text-neutral-950">92%</span>
          </div>
          <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-neutral-100">
            <motion.span
              className="block h-full rounded-full bg-gradient-to-r from-brand to-accent-purple"
              initial={{ width: 0 }}
              whileInView={{ width: "92%" }}
              viewport={VIEWPORT}
              transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
            />
          </div>
        </motion.div>
      </motion.div>
    </PreviewShell>
  );
}

// ─────────────────────────────────────────────────────────────
// BI / AI-BI/DW — real multi-series animated area chart.
// • Two series (Revenue + Cash) — each draws on view via stroke-dashoffset
// • Area fill scales up from baseline (transform-origin = bottom)
// • Y-axis grid lines for context
// • X-axis quarter labels (Q1…Q4)
// • Floating legend at the top with %-change pills
// ─────────────────────────────────────────────────────────────

const BI_SERIES_REVENUE = [42, 46, 51, 49, 56, 62, 68, 74, 79, 85, 90, 96];
const BI_SERIES_CASH = [28, 30, 33, 36, 39, 43, 47, 52, 57, 63, 70, 76];

export function BiKpiSparklinePreview() {
  const W = 240;
  const H = 90;
  const padX = 8;
  const padY = 6;
  const allValues = [...BI_SERIES_REVENUE, ...BI_SERIES_CASH];
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;

  const seriesPath = (series: readonly number[]) => {
    const pts = series.map((v, i) => {
      const x = padX + (i / (series.length - 1)) * (W - 2 * padX);
      const y = H - padY - ((v - min) / range) * (H - 2 * padY);
      return { x, y };
    });
    const line = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    const area = `${line} L ${pts[pts.length - 1].x} ${H - padY} L ${pts[0].x} ${H - padY} Z`;
    return { line, area };
  };

  const revenue = seriesPath(BI_SERIES_REVENUE);
  const cash = seriesPath(BI_SERIES_CASH);

  return (
    <PreviewShell className="w-[64%] max-w-[300px]">
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="rounded-xl border border-neutral-200/80 bg-white/95 p-2 shadow-[0_8px_24px_-12px_rgba(14,18,27,0.18)]"
      >
        {/* Legend row */}
        <motion.div variants={row} className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[8px] font-semibold text-neutral-700">
              <span className="inline-block size-1.5 rounded-full bg-brand" />
              Revenue
            </span>
            <span className="inline-flex items-center gap-1 text-[8px] font-semibold text-neutral-700">
              <span className="inline-block size-1.5 rounded-full bg-accent-purple" />
              Cash
            </span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-green/15 px-1.5 py-0.5 text-[8px] font-semibold text-accent-green">
            +18.4% YoY
          </span>
        </motion.div>

        {/* Chart */}
        <motion.svg
          variants={row}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="mt-1 block h-[80px] w-full"
          aria-hidden
        >
          <defs>
            <linearGradient id="bi-rev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(62,111,243,0.35)" />
              <stop offset="100%" stopColor="rgba(62,111,243,0)" />
            </linearGradient>
            <linearGradient id="bi-cash" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(95,87,255,0.25)" />
              <stop offset="100%" stopColor="rgba(95,87,255,0)" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[0.25, 0.5, 0.75].map((p) => (
            <line
              key={p}
              x1={padX}
              x2={W - padX}
              y1={padY + (H - 2 * padY) * p}
              y2={padY + (H - 2 * padY) * p}
              stroke="#e5e7eb"
              strokeWidth={0.4}
              strokeDasharray="2 3"
            />
          ))}

          {/* Cash area (behind) */}
          <motion.path
            d={cash.area}
            fill="url(#bi-cash)"
            initial={{ opacity: 0, scaleY: 0 }}
            whileInView={{ opacity: 1, scaleY: 1 }}
            viewport={VIEWPORT}
            style={{ transformOrigin: `0 ${H - padY}px` }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
          />
          <motion.path
            d={cash.line}
            fill="none"
            stroke="#5f57ff"
            strokeWidth={1.3}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={VIEWPORT}
            transition={{ duration: 1.4, ease: "easeOut", delay: 0.5 }}
          />

          {/* Revenue area (front, slightly more prominent) */}
          <motion.path
            d={revenue.area}
            fill="url(#bi-rev)"
            initial={{ opacity: 0, scaleY: 0 }}
            whileInView={{ opacity: 1, scaleY: 1 }}
            viewport={VIEWPORT}
            style={{ transformOrigin: `0 ${H - padY}px` }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.7 }}
          />
          <motion.path
            d={revenue.line}
            fill="none"
            stroke="#3e6ff3"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={VIEWPORT}
            transition={{ duration: 1.4, ease: "easeOut", delay: 0.7 }}
          />

          {/* Endpoint dot on revenue */}
          <motion.circle
            cx={W - padX}
            cy={
              H -
              padY -
              ((BI_SERIES_REVENUE[BI_SERIES_REVENUE.length - 1] - min) / range) *
                (H - 2 * padY)
            }
            r={2}
            fill="#3e6ff3"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={VIEWPORT}
            transition={{ delay: 1.9, duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
          />

          {/* Quarter labels */}
          {["Q1", "Q2", "Q3", "Q4"].map((q, i) => (
            <text
              key={q}
              x={padX + (i / 3) * (W - 2 * padX)}
              y={H - 0.5}
              fontSize={6}
              textAnchor={i === 0 ? "start" : i === 3 ? "end" : "middle"}
              fill="#9ca3af"
              fontFamily="monospace"
            >
              {q}
            </text>
          ))}
        </motion.svg>
      </motion.div>
    </PreviewShell>
  );
}
